import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { ApiClients } from '../../../context/ApiClientsContext';
import type { Sphere } from 'types/global';
import type { ArcInfo } from 'types/arc';
import ArcDetailOrganism from '../ArcDetail/ArcDetailOrganism';
import ArcListMolecule from '../../molecules/ArcList/ArcListMolecule';
import './SphereArcsTab.scss';

export interface SphereArcsTabProps {
  sphere: Sphere;
  organizationId: string;
  apiClients: ApiClients | null;
  hasEditPermission: boolean;
  onSphereRefresh?: () => void;
  initialArcId?: string;
}

const SphereArcsTab: React.FC<SphereArcsTabProps> = ({
  sphere,
  apiClients,
  hasEditPermission,
  onSphereRefresh,
  initialArcId,
}) => {
  const { t } = useTranslation();

  const arcs: ArcInfo[] = sphere.arcs || [];

  const defaultArcId = useMemo(() => {
    if (initialArcId && arcs.some((a) => a.id === initialArcId)) {
      return initialArcId;
    }
    const primary = arcs.find((a) => a.isPrimary);
    return primary?.id || arcs[0]?.id || '';
  }, [initialArcId, arcs]);

  const [selectedArcId, setSelectedArcId] = useState<string>(defaultArcId);

  const selectedArc = useMemo(() => {
    return arcs.find((a) => a.id === selectedArcId) || arcs[0] || null;
  }, [arcs, selectedArcId]);

  const handleArcUpdated = useCallback(() => {
    onSphereRefresh?.();
  }, [onSphereRefresh]);

  if (arcs.length === 0) {
    return (
      <div className="sphere-arcs-tab">
        <div className="sphere-arcs-tab__empty">
          <i className="pi pi-sync sphere-arcs-tab__empty-icon" />
          <p className="sphere-arcs-tab__empty-text">
            {t('sphereArcs.empty')}
          </p>
        </div>
      </div>
    );
  }

  // Single arc mode: render detail view directly
  if (arcs.length === 1) {
    return (
      <div className="sphere-arcs-tab">
        <ArcDetailOrganism
          arc={arcs[0]}
          apiClients={apiClients}
          hasEditPermission={hasEditPermission}
          onArcUpdated={handleArcUpdated}
        />
      </div>
    );
  }

  // Multi-arc mode: master list on left, detail on right
  return (
    <div className="sphere-arcs-tab sphere-arcs-tab--multi">
      <ArcListMolecule
        arcs={arcs}
        selectedArcId={selectedArc?.id || ''}
        onSelectArc={setSelectedArcId}
      />
      <div className="sphere-arcs-tab__detail">
        {selectedArc ? (
          <ArcDetailOrganism
            arc={selectedArc}
            apiClients={apiClients}
            hasEditPermission={hasEditPermission}
            onArcUpdated={handleArcUpdated}
          />
        ) : (
          <div className="sphere-arcs-tab__no-selection">
            <p>{t('sphereArcs.selectArc')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SphereArcsTab;
